import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { resolveCompanyLogoSources } from './companyLogo';

const PDF_MARGIN_MM = 12;
const LOGO_MAX_HEIGHT_MM = 12;
const LOGO_MAX_WIDTH_MM = 36;
const LOGO_CONTENT_GAP_MM = 2;
const PLACEHOLDER_EXPORT_COLOR = '#d1d5db';
const FILLED_FIELD_EXPORT_COLOR = '#111827';

function toGrayscale(canvas) {
  const ctx = canvas.getContext('2d');
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;
  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    data[i] = gray;
    data[i + 1] = gray;
    data[i + 2] = gray;
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

function clearFormValues(root) {
  root.querySelectorAll('input').forEach((input) => {
    const type = (input.type || '').toLowerCase();
    if (type === 'checkbox' || type === 'radio') {
      input.checked = false;
      return;
    }
    if (type !== 'hidden') {
      input.value = '';
    }
  });
  root.querySelectorAll('textarea').forEach((textarea) => {
    textarea.value = '';
  });
  root.querySelectorAll('select').forEach((select) => {
    select.selectedIndex = 0;
  });
}

function styleEmptyFieldPlaceholders(root) {
  root.querySelectorAll('input, textarea').forEach((field) => {
    const type = (field.type || '').toLowerCase();
    if (type === 'checkbox' || type === 'radio' || type === 'hidden') return;

    const hasValue = String(field.value || '').trim() !== '';
    const color = hasValue ? FILLED_FIELD_EXPORT_COLOR : PLACEHOLDER_EXPORT_COLOR;
    field.style.color = color;
    field.style.setProperty('-webkit-text-fill-color', color);
  });
}

function syncFormValues(source, target) {
  const sourceFields = source.querySelectorAll('input, textarea, select');
  const targetFields = target.querySelectorAll('input, textarea, select');

  for (let i = 0; i < sourceFields.length; i++) {
    const src = sourceFields[i];
    const tgt = targetFields[i];
    if (!tgt) continue;

    const type = (src.type || '').toLowerCase();
    if (type === 'checkbox' || type === 'radio') {
      tgt.checked = src.checked;
    } else if (src.tagName === 'SELECT') {
      tgt.selectedIndex = src.selectedIndex;
      tgt.value = src.value;
    } else {
      tgt.value = src.value;
    }
  }
}

function syncCanvasValues(source, target) {
  const sourceCanvases = source.querySelectorAll('canvas');
  const targetCanvases = target.querySelectorAll('canvas');
  for (let i = 0; i < sourceCanvases.length; i++) {
    const src = sourceCanvases[i];
    const tgt = targetCanvases[i];
    if (!src || !tgt) continue;
    const ctx = tgt.getContext('2d');
    if (!ctx) continue;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, tgt.width, tgt.height);
    try {
      ctx.drawImage(src, 0, 0);
    } catch {
      // Ignore tainted/empty canvas copy failures
    }
  }
}

function clearCanvasValues(root) {
  root.querySelectorAll('canvas').forEach((canvas) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  });
}

function injectFormTitle(clone, formTitle) {
  if (!formTitle) return;

  const title = document.createElement('h2');
  title.className = 'pdf-export-form-title';
  title.textContent = formTitle;
  clone.insertBefore(title, clone.firstChild);
}

function createExportClone(source, { blank, formTitle }) {
  const zoom = parseFloat(source.style.zoom) || 1;
  const unzoomedWidth = Math.max(Math.ceil(source.getBoundingClientRect().width / zoom), 1);

  const wrapper = document.createElement('div');
  wrapper.setAttribute('aria-hidden', 'true');
  wrapper.style.cssText =
    'position:fixed;left:-10000px;top:0;width:0;height:0;overflow:visible;pointer-events:none;';

  const clone = source.cloneNode(true);
  clone.style.zoom = '1';
  clone.style.width = `${unzoomedWidth}px`;
  clone.style.maxWidth = 'none';
  clone.style.overflow = 'visible';
  clone.style.boxSizing = 'border-box';

  syncFormValues(source, clone);
  syncCanvasValues(source, clone);

  clone.classList.add('pdf-export-active');
  if (blank) {
    clone.classList.add('pdf-export-blank');
  }

  injectFormTitle(clone, formTitle);
  prepareCloneForExport(clone, { blank });

  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  return { wrapper, clone };
}

function prepareCloneForExport(root, { blank }) {
  root
    .querySelectorAll('.form-heading-number, .form-item-number')
    .forEach((node) => {
      node.style.display = 'none';
    });

  // Hide Type/Draw tab controls and pad actions in PDF — keep only the signature content.
  root.querySelectorAll('.signature-field-tabs > .inline-flex').forEach((node) => {
    node.style.display = 'none';
  });
  root.querySelectorAll('.signature-pad-clear, .signature-pad-save').forEach((node) => {
    node.style.display = 'none';
  });
  root.querySelectorAll('.signature-field-tabs p').forEach((node) => {
    const text = node.textContent?.toLowerCase() || '';
    if (
      text.includes('mouse or finger') ||
      text.includes('unsaved') ||
      text.includes('signature saved') ||
      text.includes('click save')
    ) {
      node.style.display = 'none';
    }
  });

  if (blank) {
    clearFormValues(root);
    clearCanvasValues(root);
  }
  styleEmptyFieldPlaceholders(root);
}

const KEEP_TOGETHER_SELECTORS = [
  'tr',
  'thead',
  '.flex.flex-col',
  '.pdf-export-form-title',
  'h3',
  'h4',
].join(', ');

function collectKeepTogetherRects(element, scaleY) {
  const rootRect = element.getBoundingClientRect();
  const rects = [];

  element.querySelectorAll(KEEP_TOGETHER_SELECTORS).forEach((el) => {
    const r = el.getBoundingClientRect();
    const top = (r.top - rootRect.top) * scaleY;
    const bottom = (r.bottom - rootRect.top) * scaleY;
    if (bottom - top > 1) {
      rects.push({ top, bottom, height: bottom - top });
    }
  });

  return rects.sort((a, b) => a.top - b.top);
}

function scaleKeepTogetherRects(rects, scaleY) {
  return rects.map((rect) => ({
    top: rect.top * scaleY,
    bottom: rect.bottom * scaleY,
    height: rect.height * scaleY,
  }));
}

function computePageBreakPositions(canvasHeight, pageHeightPx, keepTogetherRects) {
  const breaks = [0];
  const minSlice = Math.max(pageHeightPx * 0.12, 40);

  while (breaks[breaks.length - 1] < canvasHeight - 1) {
    const start = breaks[breaks.length - 1];
    let end = Math.min(start + pageHeightPx, canvasHeight);

    if (end >= canvasHeight - 1) {
      break;
    }

    for (const rect of keepTogetherRects) {
      if (end > rect.top + 4 && end < rect.bottom - 4) {
        const adjusted = Math.floor(rect.top);
        if (adjusted - start >= minSlice) {
          end = adjusted;
        } else if (rect.bottom - start <= pageHeightPx) {
          end = Math.min(Math.ceil(rect.bottom), canvasHeight);
        }
        break;
      }
    }

    if (end <= start) {
      end = Math.min(start + pageHeightPx, canvasHeight);
    }

    if (end === start) {
      end = Math.min(start + pageHeightPx, canvasHeight);
    }

    if (end <= start) {
      break;
    }

    breaks.push(end);
  }

  return breaks;
}

function cropCanvasSlice(source, y, height) {
  const slice = document.createElement('canvas');
  slice.width = source.width;
  slice.height = height;
  const ctx = slice.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, slice.width, slice.height);
  ctx.drawImage(source, 0, y, source.width, height, 0, 0, source.width, height);
  return slice;
}

async function loadImageElement(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = url;
  });
}

function imageElementToPngLogo(img) {
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (!width || !height) return null;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  return {
    dataUrl: canvas.toDataURL('image/png'),
    format: 'PNG',
    width,
    height,
  };
}

async function blobToPngLogo(blob) {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(blob);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bitmap, 0, 0);
      return {
        dataUrl: canvas.toDataURL('image/png'),
        format: 'PNG',
        width: canvas.width,
        height: canvas.height,
      };
    } finally {
      bitmap.close();
    }
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await loadImageElement(objectUrl);
    return imageElementToPngLogo(img);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function tryLoadLogoFromSource({ url, userEmail }) {
  if (!url) return null;
  try {
    const headers = {};
    if (userEmail) {
      headers['X-User-Email'] = userEmail;
    }
    const res = await fetch(url, { headers, credentials: 'include' });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.size) return null;
    return blobToPngLogo(blob);
  } catch {
    return null;
  }
}

async function loadLogoForPdf(sources) {
  if (!sources?.length) return null;
  for (const source of sources) {
    const logo = await tryLoadLogoFromSource(source);
    if (logo) return logo;
  }
  return null;
}

function getLogoDimensions(logo) {
  let width = LOGO_MAX_WIDTH_MM;
  let height = (logo.height / logo.width) * width;
  if (height > LOGO_MAX_HEIGHT_MM) {
    height = LOGO_MAX_HEIGHT_MM;
    width = (logo.width / logo.height) * height;
  }
  return { width, height };
}

function addLogoToPdfPage(pdf, logo, pageWidth, sideMargin, logoDims) {
  if (!logo?.dataUrl) return;
  const { width, height } = logoDims || getLogoDimensions(logo);
  const x = pageWidth - sideMargin - width;
  const y = sideMargin;
  pdf.addImage(logo.dataUrl, 'PNG', x, y, width, height);
}

function addCanvasToPdf(pdf, canvas, captureHeight, keepTogetherRects, logo) {
  toGrayscale(canvas);

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const sideMargin = PDF_MARGIN_MM;
  const logoDims = logo ? getLogoDimensions(logo) : null;
  const topMargin = logoDims
    ? sideMargin + logoDims.height + LOGO_CONTENT_GAP_MM
    : sideMargin;
  const bottomMargin = sideMargin;
  const contentWidth = pageWidth - sideMargin * 2;
  const contentHeight = pageHeight - topMargin - bottomMargin;

  const scaleY = canvas.height / Math.max(captureHeight, 1);
  const scaledRects = scaleKeepTogetherRects(keepTogetherRects, scaleY);
  const pageHeightPx = (contentHeight / contentWidth) * canvas.width;
  const breaks = computePageBreakPositions(canvas.height, pageHeightPx, scaledRects);

  for (let i = 0; i < breaks.length; i++) {
    const sourceY = Math.round(breaks[i]);
    const nextY = i + 1 < breaks.length ? Math.round(breaks[i + 1]) : canvas.height;
    const sourceH = nextY - sourceY;
    if (sourceH <= 0) continue;

    if (i > 0) pdf.addPage();

    const slice = cropCanvasSlice(canvas, sourceY, sourceH);
    const imgData = slice.toDataURL('image/jpeg', 0.92);
    const imgHeightMm = (sourceH * contentWidth) / canvas.width;
    pdf.addImage(imgData, 'JPEG', sideMargin, topMargin, contentWidth, imgHeightMm);
    addLogoToPdfPage(pdf, logo, pageWidth, sideMargin, logoDims);
  }
}

function addAttachmentImagesToPdf(pdf, attachmentImages = []) {
  const images = (attachmentImages || []).filter(Boolean);
  if (!images.length) return;

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = PDF_MARGIN_MM;
  const contentWidth = pageWidth - margin * 2;
  const contentHeight = pageHeight - margin * 2;
  const gap = 4;
  const cols = 2;
  const cellWidth = (contentWidth - gap) / cols;
  const cellHeight = (contentHeight - gap) / 2;

  images.forEach((dataUrl, index) => {
    if (index % 4 === 0) {
      pdf.addPage();
      pdf.setFontSize(12);
      pdf.setTextColor(40);
      pdf.text(index === 0 ? 'Attached images' : 'Attached images (continued)', margin, margin - 2);
    }

    const slot = index % 4;
    const col = slot % cols;
    const row = Math.floor(slot / cols);
    const x = margin + col * (cellWidth + gap);
    const y = margin + 4 + row * (cellHeight + gap);

    try {
      const props = pdf.getImageProperties(dataUrl);
      const ratio = Math.min(cellWidth / props.width, (cellHeight - 4) / props.height);
      const drawW = props.width * ratio;
      const drawH = props.height * ratio;
      const drawX = x + (cellWidth - drawW) / 2;
      const drawY = y + (cellHeight - 4 - drawH) / 2;
      const format = String(dataUrl).startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
      pdf.addImage(dataUrl, format, drawX, drawY, drawW, drawH);
    } catch {
      // Skip unreadable images rather than failing the whole export.
    }
  });
}

/**
 * Capture a form DOM node and return a black-and-white multi-page PDF blob.
 * @param {HTMLElement} element
 * @param {{ blank?: boolean, companyLogoUrl?: string | null, userEmail?: string | null, formTitle?: string | null, attachmentImages?: string[] }} options
 */
export async function exportFormElementToPdf(
  element,
  {
    blank = false,
    companyLogoUrl = null,
    userEmail = null,
    formTitle = null,
    attachmentImages = [],
  } = {}
) {
  if (!element) {
    throw new Error('Form element not found');
  }

  const logoSources = await resolveCompanyLogoSources(userEmail, companyLogoUrl);
  const logo = await loadLogoForPdf(logoSources);

  const { wrapper, clone } = createExportClone(element, { blank, formTitle });

  try {
    await new Promise((resolve) => requestAnimationFrame(() => resolve()));

    const captureWidth = Math.max(
      Math.ceil(clone.scrollWidth),
      Math.ceil(clone.offsetWidth),
      1
    );
    const captureHeight = Math.max(
      Math.ceil(clone.scrollHeight),
      Math.ceil(clone.offsetHeight),
      1
    );

    const keepTogetherRects = collectKeepTogetherRects(clone, 1);

    const canvas = await html2canvas(clone, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: captureWidth,
      height: captureHeight,
      scrollX: 0,
      scrollY: 0,
      windowWidth: captureWidth,
      windowHeight: captureHeight,
      ignoreElements: (node) =>
        node?.hasAttribute?.('data-form-submit-btn') ||
        node?.hasAttribute?.('data-form-print-btn'),
    });

    const pdf = new jsPDF('p', 'mm', 'a4');
    addCanvasToPdf(pdf, canvas, captureHeight, keepTogetherRects, logo);
    addAttachmentImagesToPdf(pdf, attachmentImages);

    return pdf.output('blob');
  } catch (err) {
    const message = err?.message || 'PDF export failed';
    throw new Error(
      message.toLowerCase().includes('canvas') || message.toLowerCase().includes('memory')
        ? 'This form is too large to export. Please try again.'
        : message
    );
  } finally {
    wrapper.remove();
  }
}
